import { Tooltip } from "@mui/material";
import { useHistory } from "react-router-dom";

import type { SourceProvenanceReference } from "../../services/ArtifactService";
import { manifestPageLabel } from "../../utils/manifestPage";
import { canOpenArtifactRef, openArtifactSource } from "./artifactSourceNav";

interface ArtifactCitationLinkProps {
  /** Dialogue-global reference number to render (the manifest's `n`). */
  citationNumber: number | null;
  /** The chunk_id carried in the <cite> tag, used to resolve the reference. */
  chunkId: number | null;
  /** chunk_id -> manifest reference, built from the artifact provenance. */
  refsByChunk: Map<number, SourceProvenanceReference>;
  /** All references, for co-cited annotation grouping on open. */
  references: SourceProvenanceReference[];
}

/**
 * Inline `[n]` citation marker rendered inside a generated document's body
 * (#20423 Story 5 — "inline source references using the same format as chat
 * responses"). Clicking opens the original source passage in the annotated
 * viewer, the same deep-link the "FROM [n]" reference list uses.
 *
 * A marker with no resolvable reference (chunk not in the manifest, or a
 * reference the reviewer can't open) renders as inert text so numbering never
 * shifts and a redacted source never becomes a broken link.
 */
const ArtifactCitationLink: React.FC<ArtifactCitationLinkProps> = ({
  citationNumber,
  chunkId,
  refsByChunk,
  references,
}) => {
  const history = useHistory();

  if (citationNumber == null) return null;

  const ref = chunkId != null ? refsByChunk.get(chunkId) : undefined;
  const clickable = ref != null && canOpenArtifactRef(ref);

  const label = `[${citationNumber}]`;
  if (!clickable) {
    return (
      <sup style={{ fontWeight: 600, color: "var(--text-secondary, #667085)" }}>
        {label}
      </sup>
    );
  }

  // `page` is `number | number[]` (MRE #30376) — never interpolate it raw.
  const pageLabel = manifestPageLabel(ref.page);

  return (
    <Tooltip
      title={
        ref?.document_name
          ? `Open ${ref.document_name}${pageLabel ? `, p. ${pageLabel}` : ""}`
          : "Open the original source passage"
      }
      arrow
    >
      <sup
        role="button"
        tabIndex={0}
        data-testid={`aa-artifact-cite-${citationNumber}`}
        onClick={() => openArtifactSource(history, ref, references)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openArtifactSource(history, ref, references);
          }
        }}
        style={{
          cursor: "pointer",
          fontWeight: 700,
          color: "var(--color-primary, #2395b7)",
        }}
      >
        {label}
      </sup>
    </Tooltip>
  );
};

export default ArtifactCitationLink;
